import React, { useCallback, useEffect, useState } from 'react';
import { FiLoader, FiTrash2, FiUsers } from 'react-icons/fi';
import useAdminApi from '../hooks/useAdminApi';
import '../styles/friendsSection.scss';

const STATUS = {
    accepted: { label: 'صديق', className: 'accepted' },
    pending: { label: 'بانتظار موافقة السائق', className: 'pending' },
    rejected: { label: 'مرفوض', className: 'rejected' },
};

const ENDPOINTS = {
    user: {
        list: (id) => `/api/admin/users/${id}/friends`,
        remove: (fid) => `/api/admin/users/friends/${fid}`,
    },
    driver: {
        list: (id) => `/api/admin/car-tows/drivers/${id}/friends`,
        remove: (fid) => `/api/admin/car-tows/drivers/friends/${fid}`,
    },
};

const formatDate = (value) => (value ? new Date(value).toLocaleDateString('en-GB') : '—');

// Friends list inside the user / driver details modal (Users.jsx, CarTows.jsx). `ownerType` is
// 'user' or 'driver'; each row shows the other side of the friendship and can be removed.
const FriendsSection = ({ ownerType, ownerId }) => {
    const { request, notify } = useAdminApi();
    const [friends, setFriends] = useState([]);
    const [loading, setLoading] = useState(false);
    const [removingId, setRemovingId] = useState(null);
    const api = ENDPOINTS[ownerType];

    const load = useCallback(async () => {
        if (!ownerId || !api) return;
        setLoading(true);
        const json = await request(api.list(ownerId));
        setFriends(Array.isArray(json?.friends) ? json.friends : []);
        setLoading(false);
    }, [ownerId, api, request]);

    useEffect(() => {
        load();
    }, [load]);

    const remove = async (friendship) => {
        const other = ownerType === 'user' ? friendship.driver?.driverName : friendship.user?.userName;
        const verb = friendship.status === 'accepted' ? 'حذف الصداقة مع' : 'حذف الطلب مع';
        if (!window.confirm(`${verb} ${other || 'هذا الحساب'}؟`)) return;
        setRemovingId(friendship._id);
        const json = await request(api.remove(friendship._id), { method: 'DELETE' });
        setRemovingId(null);
        if (json) {
            notify(json.message || 'تم حذف الصداقة');
            setFriends((prev) => prev.filter((f) => f._id !== friendship._id));
        }
    };

    // Accepted first, then pending, then rejected (the driver never sees rejected ones).
    const order = { accepted: 0, pending: 1, rejected: 2 };
    const sorted = [...friends].sort((a, b) => (order[a.status] ?? 3) - (order[b.status] ?? 3));
    const acceptedCount = friends.filter((f) => f.status === 'accepted').length;

    return (
        <div className="info-group friends-section">
            <div className="friends-header">
                <h3><FiUsers /> الأصدقاء</h3>
                <span className="friends-count">{acceptedCount} صديق{friends.length > acceptedCount ? ` · ${friends.length - acceptedCount} طلب` : ''}</span>
            </div>
            {loading ? (
                <div className="friends-empty"><FiLoader className="spinner" /> جاري التحميل...</div>
            ) : sorted.length === 0 ? (
                <div className="friends-empty">لا يوجد أصدقاء</div>
            ) : (
                <ul className="friends-list">
                    {sorted.map((f) => {
                        const other = ownerType === 'user' ? f.driver : f.user;
                        const status = STATUS[f.status] || { label: f.status, className: '' };
                        return (
                            <li key={f._id} className="friend-row">
                                <div className="friend-main">
                                    <div className="friend-name">
                                        {ownerType === 'user' && f.status === 'accepted' ? (
                                            <span
                                                className={`presence-dot ${other?.online ? 'online' : ''}`}
                                                title={other?.online ? 'متاح الآن' : 'غير متاح'}
                                            />
                                        ) : null}
                                        {ownerType === 'user'
                                            ? (other?.driverName || 'سائق محذوف')
                                            : (other?.userName || 'مستخدم محذوف')}
                                        {ownerType === 'user' && other?.driverNumber ? <span className="friend-number">#{other.driverNumber}</span> : null}
                                    </div>
                                    <div className="friend-meta">
                                        <span dir="ltr">{other?.phoneNumber || '—'}</span>
                                        <span>طلب: {formatDate(f.requestedAt)}</span>
                                        {f.respondedAt ? <span>{f.status === 'accepted' ? 'قبول' : 'رد'}: {formatDate(f.respondedAt)}</span> : null}
                                    </div>
                                </div>
                                <span className={`friend-status ${status.className}`}>{status.label}</span>
                                <button
                                    type="button"
                                    className="friend-remove"
                                    onClick={() => remove(f)}
                                    disabled={removingId === f._id}
                                    title="حذف"
                                >
                                    {removingId === f._id ? <FiLoader className="spinner" /> : <FiTrash2 />}
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
};

export default FriendsSection;
